/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { PlaybackContentType } from "../../../videoplayer/enums";
import { AiringPreviewUtils } from "../../../videoplayer/utils/AiringPreviewUtils";
import { StreamType, VideoPlayerName, VodAssetType } from "../enums";
import type {
  TConfiguration,
  TEntitlement,
  TOperationalSnapshotInputs,
  TPgd,
  TPgdDetails,
  TPlaybackSessionContext,
  TSessionState
} from "../types";
import type { TPlayerStats, TVariantTrack } from "../../../videoplayer/types";

const GREATEST_COMMON_DIVISOR = (left: number, right: number): number => {
  let dividend = left;
  let divisor = right;
  while (divisor !== 0) {
    const remainder = dividend % divisor;
    dividend = divisor;
    divisor = remainder;
  }
  return dividend;
};

const findActiveVariant = (
  variants: TVariantTrack[] | undefined
): TVariantTrack | undefined => {
  if (variants === undefined) {
    return undefined;
  }
  return variants.find((variant) => variant.active);
};

const getMaxChannelDimensions = (
  variants: TVariantTrack[] | undefined
): string | undefined => {
  if (variants === undefined) {
    return undefined;
  }
  let maxVariant: TVariantTrack | undefined;
  for (const variant of variants) {
    if (variant.width === null || variant.height === null) {
      continue;
    }
    if (
      maxVariant === undefined ||
      maxVariant.width === null ||
      maxVariant.height === null ||
      variant.width * variant.height > maxVariant.width * maxVariant.height
    ) {
      maxVariant = variant;
    }
  }
  return maxVariant !== undefined &&
    maxVariant.width !== null &&
    maxVariant.height !== null
    ? `${maxVariant.width}x${maxVariant.height}`
    : undefined;
};

const mapStreamingProtocol = (
  transportEncodingType: string | undefined,
  streamingUrl: string | null
): string | undefined => {
  const normalizedEncoding =
    transportEncodingType !== undefined
      ? transportEncodingType.toLowerCase()
      : "";
  if (normalizedEncoding.indexOf("hls") !== -1) {
    return "HLS";
  }
  if (normalizedEncoding.indexOf("dash") !== -1) {
    return "DASH";
  }
  if (streamingUrl === null) {
    return undefined;
  }
  const normalizedUrl = streamingUrl.toLowerCase();
  if (normalizedUrl.indexOf(".m3u8") !== -1) {
    return "HLS";
  }
  if (normalizedUrl.indexOf(".mpd") !== -1) {
    return "DASH";
  }
  return undefined;
};

const mapContainerFormat = (
  transportEncodingType: string | undefined
): string | undefined => {
  if (transportEncodingType === undefined) {
    return undefined;
  }
  const normalizedEncoding = transportEncodingType.toLowerCase();
  if (normalizedEncoding.indexOf("fmp4") !== -1) {
    return "fMP4";
  }
  return normalizedEncoding.indexOf("transportstream") !== -1
    ? "MPEG-2 TS"
    : undefined;
};

const getResolution = (stats: TPlayerStats): string | undefined => {
  return Number.isFinite(stats.height) && stats.height > 0
    ? `${Math.round(stats.height)}p`
    : undefined;
};

const getAspectRatio = (stats: TPlayerStats): string | undefined => {
  if (
    !Number.isFinite(stats.width) ||
    !Number.isFinite(stats.height) ||
    stats.width <= 0 ||
    stats.height <= 0
  ) {
    return undefined;
  }
  const width = Math.round(stats.width);
  const height = Math.round(stats.height);
  const divisor = GREATEST_COMMON_DIVISOR(width, height);
  return `${width / divisor}:${height / divisor}`;
};

/** `session_type` (spec §8/§15) — mirrors Android's exact casing. */
const mapContentTypeToSessionType = (
  contentType: PlaybackContentType
): string => {
  switch (contentType) {
    case PlaybackContentType.LIVE:
      return "LiveTv";
    case PlaybackContentType.NDVR_LIVE:
    case PlaybackContentType.NDVR_RECORDED:
      return "Dvr";
    case PlaybackContentType.STARTOVER_BEGINNING:
    case PlaybackContentType.STARTOVER_RESUME:
      return "StartOver";
    case PlaybackContentType.CATCHUP_BEGINNING:
    case PlaybackContentType.CATCHUP_RESUME:
      return "CatchUp";
    case PlaybackContentType.VOD:
    default:
      return "Vod";
  }
};

/** `video_source` (spec §15 matrix). */
const mapContentTypeToVideoSource = (
  contentType: PlaybackContentType
): string => {
  switch (contentType) {
    case PlaybackContentType.LIVE:
      return "Iptv";
    case PlaybackContentType.NDVR_LIVE:
    case PlaybackContentType.NDVR_RECORDED:
      return "nDVR";
    default:
      return "Vod";
  }
};

/**
 * `asset_bounds` (spec §15 matrix). Live TV is treated as `"Caching"`
 * (the MSO-IPTV case) — Web's `PlaybackContentType` cannot distinguish the
 * linearized-live-TV `"Fixed"` variant from the same `LIVE` value.
 */
const mapContentTypeToAssetBounds = (
  contentType: PlaybackContentType
): string => {
  switch (contentType) {
    case PlaybackContentType.LIVE:
      return "Caching";
    case PlaybackContentType.NDVR_LIVE:
      return "Growing";
    default:
      return "Fixed";
  }
};

/**
 * Builds the `configuration` wire group — present on `playbackStart`/
 * `playbackHealth`/`playbackEnd` only; omitted from `playbackSession`
 * (spec §4/§8).
 */
export const buildConfiguration = (
  context: TPlaybackSessionContext,
  inputs?: TOperationalSnapshotInputs,
  stats?: TPlayerStats
): TConfiguration => {
  const preview =
    context.contentType === PlaybackContentType.VOD
      ? context.metadata.vodOfferPreview
      : context.metadata.offerPreview;
  const activeVariant = findActiveVariant(
    inputs !== undefined ? inputs.variants : undefined
  );
  const transportEncodingType =
    inputs !== undefined ? inputs.transportEncodingType : undefined;
  return {
    sessionType: mapContentTypeToSessionType(context.contentType),
    streamType: StreamType.IPTV,
    videoSource: mapContentTypeToVideoSource(context.contentType),
    assetBounds: mapContentTypeToAssetBounds(context.contentType),
    videoPlayer: VideoPlayerName.SHAKA_PLAYER,
    isPayPerView:
      preview !== undefined && preview.isPpv !== undefined
        ? preview.isPpv
        : undefined,
    connectionMode: inputs !== undefined ? inputs.connectionMode : undefined,
    streamingProtocol: mapStreamingProtocol(
      transportEncodingType,
      context.streamingUrl
    ),
    regionMode: inputs !== undefined ? inputs.regionMode : undefined,
    maxChannelDimensions: getMaxChannelDimensions(
      inputs !== undefined ? inputs.variants : undefined
    ),
    drmType: inputs !== undefined ? inputs.drmType : undefined,
    videoCodec:
      activeVariant !== undefined && activeVariant.videoCodec !== null
        ? activeVariant.videoCodec
        : undefined,
    audioCodec:
      activeVariant !== undefined && activeVariant.audioCodec !== null
        ? activeVariant.audioCodec
        : undefined,
    containerFormat: mapContainerFormat(transportEncodingType),
    resolution: stats !== undefined ? getResolution(stats) : undefined,
    aspectRatio: stats !== undefined ? getAspectRatio(stats) : undefined
  };
};

/**
 * Builds an AiringPreviewUtils instance appropriate for the content type —
 * linear/SOCU/nDVR read `airingOffers` + `offerPreview`; VOD reads only
 * `vodOfferPreview` (per spec §4, "VOD: IPlayable.vodPlaybackModel +
 * metadata.vodOfferPreview").
 */
const buildPreview = (context: TPlaybackSessionContext): AiringPreviewUtils => {
  const isVod = context.contentType === PlaybackContentType.VOD;
  return new AiringPreviewUtils(
    isVod ? undefined : context.metadata.airingOffers,
    isVod ? context.metadata.vodOfferPreview : context.metadata.offerPreview
  );
};

/**
 * Whether the metadata needed to resolve real PGD titles has loaded yet, as
 * opposed to the async metadata fetch still being in flight (in which case
 * `AiringPreviewUtils` falls back to the "Title Not Available" placeholder).
 * Used to defer `playbackStart` until titles resolve — spec §9's worked
 * example expects a resolved title in the start payload, not the placeholder.
 */
export const isPgdMetadataResolved = (
  context: TPlaybackSessionContext
): boolean => {
  const { metadata, contentType } = context;
  if (contentType === PlaybackContentType.VOD) {
    return metadata.vodOfferPreview !== undefined;
  }
  return (
    (metadata.airingOffers !== undefined && metadata.airingOffers.length > 0) ||
    metadata.offerPreview !== undefined
  );
};

const resolveMediaId = (
  context: TPlaybackSessionContext
): string | undefined => {
  const { playable } = context;
  if (playable.vodPlaybackModel !== undefined) {
    return playable.vodPlaybackModel.id;
  }
  if (playable.offerId !== undefined) {
    return playable.offerId;
  }
  return playable.channel !== undefined
    ? playable.channel.channelId
    : undefined;
};

const resolveProviderId = (
  context: TPlaybackSessionContext
): string | undefined => {
  const { metadata, playable, contentType } = context;
  if (contentType === PlaybackContentType.VOD) {
    return playable.vodPlaybackModel !== undefined
      ? playable.vodPlaybackModel.videoProviderId
      : undefined;
  }
  if (
    contentType === PlaybackContentType.NDVR_LIVE ||
    contentType === PlaybackContentType.NDVR_RECORDED
  ) {
    const recordingProviderPartnerId =
      metadata.watchAction !== undefined &&
      metadata.watchAction.kernel !== undefined
        ? metadata.watchAction.kernel.recordingProviderPartnerId
        : undefined;
    return typeof recordingProviderPartnerId === "string"
      ? recordingProviderPartnerId
      : undefined;
  }
  if (
    contentType === PlaybackContentType.STARTOVER_BEGINNING ||
    contentType === PlaybackContentType.STARTOVER_RESUME ||
    contentType === PlaybackContentType.CATCHUP_BEGINNING ||
    contentType === PlaybackContentType.CATCHUP_RESUME
  ) {
    return metadata.watchAction !== undefined &&
      metadata.watchAction.kernel !== undefined
      ? metadata.watchAction.kernel.partnerId
      : undefined;
  }
  return playable.channel !== undefined
    ? playable.channel.linearProviderPartnerId
    : undefined;
};

const mapEntitlementToVodAssetType = (
  entitlement: string | undefined
): string | undefined => {
  switch (entitlement) {
    case "Free":
    case "freeNoPurchase":
    case "freeZeroPurchasable":
      return VodAssetType.FREE;
    case "Rentable":
    case "Rented":
    case "rentable":
    case "rented":
    case "purchasable":
      return VodAssetType.TRANSACTIONAL;
    case "Subscribable":
    case "Subscribed":
    case "subscribable":
    case "subscribed":
      return VodAssetType.SUBSCRIPTION;
    default:
      return undefined;
  }
};

const readPreviewString = (
  previewData: unknown,
  key: string
): string | undefined => {
  if (
    previewData === null ||
    previewData === undefined ||
    typeof previewData !== "object"
  ) {
    return undefined;
  }
  const value = (previewData as Record<string, unknown>)[key];
  return typeof value === "string" && value !== "" ? value : undefined;
};

const getActivePreview = (
  context: TPlaybackSessionContext
): unknown | undefined => {
  return context.contentType === PlaybackContentType.VOD
    ? context.metadata.vodOfferPreview
    : context.metadata.offerPreview;
};

const resolveContentTypeLabel = (
  context: TPlaybackSessionContext,
  isEpisode: boolean
): string | undefined => {
  if (isEpisode) {
    return "Episode";
  }
  const normalizeContentType = (rawType: string): string | undefined => {
    const normalized = rawType.trim().toLowerCase();
    if (normalized === "") {
      return undefined;
    }
    if (normalized.indexOf("movie") !== -1) {
      return "Movie";
    }
    if (normalized.indexOf("series") !== -1 || normalized === "show") {
      return "Series";
    }
    if (normalized.indexOf("episode") !== -1) {
      return "Episode";
    }
    if (
      normalized.indexOf("sport") !== -1 ||
      normalized.indexOf("game") !== -1 ||
      normalized.indexOf("match") !== -1 ||
      normalized.indexOf("team") !== -1 ||
      normalized.indexOf("league") !== -1
    ) {
      return "Sports";
    }
    return undefined;
  };

  const activePreview = getActivePreview(context);
  const previewType = readPreviewString(activePreview, "previewType");
  const categoryLabel = readPreviewString(activePreview, "categoryLabel");
  const normalizedPreviewType =
    previewType !== undefined ? normalizeContentType(previewType) : undefined;
  if (normalizedPreviewType !== undefined) {
    return normalizedPreviewType;
  }
  const normalizedCategoryLabel =
    categoryLabel !== undefined
      ? normalizeContentType(categoryLabel)
      : undefined;
  if (normalizedCategoryLabel !== undefined) {
    return normalizedCategoryLabel;
  }
  return previewType || categoryLabel;
};

const resolveGenreLabel = (
  context: TPlaybackSessionContext,
  preview: AiringPreviewUtils
): string | undefined => {
  const genre = preview.getGenre();
  if (typeof genre === "string" && genre !== "") {
    return genre;
  }
  const activePreview = getActivePreview(context);
  return readPreviewString(activePreview, "categoryLabel");
};

export const buildPgd = (
  context: TPlaybackSessionContext,
  providerId?: string
): TPgd => {
  const preview = buildPreview(context);
  const channel = context.playable.channel;
  const contentTitle = preview.getChannelTitle();
  const episodeTitle = preview.getEpisodeTitle();

  return {
    collectionId: context.playable.contentId,
    collectionTitle:
      episodeTitle !== "" ? episodeTitle : contentTitle || undefined,
    contentId: resolveMediaId(context),
    contentTitle: contentTitle !== "" ? contentTitle : undefined,
    providerId:
      providerId !== undefined && providerId !== ""
        ? providerId
        : resolveProviderId(context),
    stationId: channel !== undefined ? channel.stationId : undefined,
    channelId: channel !== undefined ? channel.channelId : undefined,
    channelCallSign: channel !== undefined ? channel.callSign : undefined,
    channelNumber: channel !== undefined ? channel.channelNumber : undefined
  };
};

export const buildPgdDetails = (
  context: TPlaybackSessionContext
): TPgdDetails => {
  const preview = buildPreview(context);
  const channel = context.playable.channel;
  const isLinearLike = context.contentType !== PlaybackContentType.VOD;
  const seasonNumber = preview.getSeasonNumber();
  const episodeNumber = preview.getEpisodeNumber();
  const isEpisode = seasonNumber !== undefined && episodeNumber !== undefined;
  const contentTypeLabel = resolveContentTypeLabel(context, isEpisode);
  const duration = preview.getDuration();
  const year = preview.getMovieYear();

  return {
    programStartTime: isLinearLike ? preview.getStartTimeEpoch() : undefined,
    contentDuration: duration > 0 ? duration : undefined,
    contentType: contentTypeLabel,
    contentNetwork: channel !== undefined ? channel.affiliate : undefined,
    seasonNumber,
    episodeNumber,
    originalAirDate: preview.getOriginalAirDate(),
    year: year > 0 ? year : undefined,
    genre: resolveGenreLabel(context, preview)
  };
};

export const buildSessionState = (
  context: TPlaybackSessionContext,
  playbackId: string,
  startTimeEpochSeconds: number,
  isClosedCaptionActive: boolean
): TSessionState => {
  return {
    playbackId,
    startTime: startTimeEpochSeconds,
    videoPlayerUrl:
      context.streamingUrl !== null ? context.streamingUrl : undefined,
    mediaId: resolveMediaId(context),
    closedCaptionEnabled: isClosedCaptionActive
  };
};

export const buildEntitlement = (
  context: TPlaybackSessionContext
): TEntitlement => {
  if (context.contentType !== PlaybackContentType.VOD) {
    return {};
  }
  const preview = context.metadata.vodOfferPreview;
  return {
    vodAssetType: mapEntitlementToVodAssetType(
      preview !== undefined ? preview.entitlement : undefined
    ),
    locality: context.locality
  };
};
